import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { ChevronLeft } from 'lucide-react';
import { setPageTitle } from '../utils/pageTitle';

const TermsOfService: React.FC = () => {
  useEffect(() => {
    setPageTitle('Terms of Service');
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  // Function to handle anchor links
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <div className="mb-6">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
        </div>

        <Card className="p-6 md:p-8 shadow-lg">
          {/* Header */}
          <div className="mb-8 border-b pb-6 dark:border-gray-700">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Terms of Service</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Welcome to the Terms of Service for JLS Apps. These terms govern your use of our applications and services. 
              By accessing or using our services, you agree to be bound by these terms.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Latest update: March 03, 2025
            </p>
          </div>

          {/* Table of Contents */}
          <div className="mb-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Table of Contents</h2>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => scrollToSection('agreement')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Agreement to Terms
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Services Description
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('accounts')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  User Accounts
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('acceptable-use')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Acceptable Use Policy
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('intellectual-property')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Intellectual Property Rights
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('third-party')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Third-Party Services
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('liability')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Limitation of Liability
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('termination')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Termination
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('changes')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Changes to Terms
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('governing-law')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Governing Law
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Contact Information
                </button>
              </li>
            </ul>
          </div>

          {/* Owner and Data Controller */}
          <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Owner and Service Provider</h2>
            <div className="text-gray-700 dark:text-gray-300">
              <p className="font-medium">JLS Trading Co</p>
              <p>5301 Terminal Street</p>
              <p>Charlotte NC 28208</p>
              <p className="mt-2">
                <span className="font-medium">Contact email:</span>{' '}
                <a 
                  href="mailto:rdhpndh@gmail.com" 
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  rdhpndh@gmail.com
                </a>
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            {/* Agreement to Terms Section */}
            <section id="agreement">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Agreement to Terms</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  By accessing or using the JLS Apps services, you agree to be bound by these Terms of Service and all applicable laws and regulations. 
                  If you do not agree with any of these terms, you are prohibited from using or accessing our services.
                </p>
                <p>
                  These Terms of Service apply to all users of the service, including without limitation users who are browsers, customers, merchants, 
                  vendors and/or content contributors.
                </p>
              </div>
            </section>

            {/* Services Description Section */}
            <section id="services">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Services Description</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  JLS Apps provides a suite of web-based applications designed to enhance productivity, collaboration, and business operations. 
                  Our services include, but are not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <span className="font-medium">Good Things Tracker:</span> A recognition and appreciation tracking application that allows teams to 
                    celebrate achievements and share positive feedback.
                  </li>
                  <li>
                    <span className="font-medium">Listing Management System:</span> A product optimization and marketplace integration tool for managing 
                    product listings across multiple platforms.
                  </li>
                  <li>
                    <span className="font-medium">PLMS (Personal Learning Management System):</span> A tool for tracking and managing personal learning 
                    progress and educational goals.
                  </li>
                  <li>
                    <span className="font-medium">DayAway Board:</span> A time-off management and team scheduling application to coordinate team 
                    availability and time-off requests.
                  </li>
                  <li>
                    <span className="font-medium">Ideogram:</span> An image generation and prompt management tool for creative content development.
                  </li>
                  <li>
                    <span className="font-medium">SOP Central:</span> A system for managing and accessing Standard Operating Procedures.
                  </li>
                </ul>
                <p>
                  We reserve the right to modify or discontinue, temporarily or permanently, any service (or any part thereof) with or without notice. 
                  We shall not be liable to you or to any third party for any modification, suspension, or discontinuance of the service.
                </p>
              </div>
            </section>

            {/* User Accounts Section */}
            <section id="accounts">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">User Accounts</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  To access certain features of our services, you may be required to create an account. You are responsible for maintaining the 
                  confidentiality of your account information, including your password, and for all activity that occurs under your account.
                </p>
                <p>
                  You agree to notify us immediately of any unauthorized use of your account or any other breach of security. We cannot and will not 
                  be liable for any loss or damage arising from your failure to comply with this section.
                </p>
                <p>
                  We use Google Authentication for secure access to our applications. By creating an account, you agree to provide accurate, current, 
                  and complete information as may be prompted by any registration forms.
                </p>
                <p>
                  Access to certain applications may be restricted based on your organization's policies and permissions. We reserve the right to 
                  refuse service, terminate accounts, or restrict access at our sole discretion.
                </p>
              </div>
            </section>

            {/* Acceptable Use Policy Section */}
            <section id="acceptable-use">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Acceptable Use Policy</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  You agree to use our services only for lawful purposes and in accordance with these Terms of Service. You agree not to use our services:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    In any way that violates any applicable federal, state, local, or international law or regulation.
                  </li>
                  <li>
                    To transmit, or procure the sending of, any advertising or promotional material, including any "junk mail," "chain letter," "spam," 
                    or any other similar solicitation.
                  </li>
                  <li>
                    To impersonate or attempt to impersonate JLS Trading Co, a JLS employee, another user, or any other person or entity.
                  </li>
                  <li>
                    To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the services, or which may harm JLS Trading Co 
                    or users of the services.
                  </li>
                  <li>
                    To attempt to gain unauthorized access to, interfere with, damage, or disrupt any parts of the services, the server on which the 
                    services are stored, or any server, computer, or database connected to the services.
                  </li>
                </ul>
                <p>
                  We reserve the right to terminate or suspend your access to all or part of the services, without prior notice, for any conduct that 
                  we, in our sole discretion, believe is in violation of any applicable law or is harmful to the interests of another user, a third-party, 
                  or us.
                </p>
              </div>
            </section>

            {/* Intellectual Property Rights Section */}
            <section id="intellectual-property">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Intellectual Property Rights</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  The services and their entire contents, features, and functionality (including but not limited to all information, software, text, 
                  displays, images, video, and audio, and the design, selection, and arrangement thereof) are owned by JLS Trading Co, its licensors, 
                  or other providers of such material and are protected by United States and international copyright, trademark, patent, trade secret, 
                  and other intellectual property or proprietary rights laws.
                </p>
                <p>
                  These Terms of Service permit you to use the services for your personal, non-commercial use only. You must not reproduce, distribute, 
                  modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on 
                  our services, except as follows:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Your computer may temporarily store copies of such materials in RAM incidental to your accessing and viewing those materials.
                  </li>
                  <li>
                    You may store files that are automatically cached by your Web browser for display enhancement purposes.
                  </li>
                  <li>
                    You may print or download one copy of a reasonable number of pages of the services for your own personal, non-commercial use and not 
                    for further reproduction, publication, or distribution.
                  </li>
                </ul>
                <p>
                  If you print, copy, modify, download, or otherwise use or provide any other person with access to any part of the services in breach of 
                  the Terms of Service, your right to use the services will stop immediately and you must, at our option, return or destroy any copies of 
                  the materials you have made.
                </p>
                <p>
                  No right, title, or interest in or to the services or any content on the services is transferred to you, and all rights not expressly 
                  granted are reserved by JLS Trading Co. Any use of the services not expressly permitted by these Terms of Service is a breach of these 
                  Terms of Service and may violate copyright, trademark, and other laws.
                </p>
              </div>
            </section>

            {/* Third-Party Services Section */}
            <section id="third-party">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Third-Party Services</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  Our services may integrate with or contain links to third-party websites, services, or resources that are not owned or controlled by 
                  JLS Trading Co. We do not endorse or assume any responsibility for any such third-party sites, information, materials, products, or services.
                </p>
                <p>
                  JLS Apps utilizes several third-party services to provide our functionality, including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <span className="font-medium">Google Services:</span> For authentication, data storage (Google Sheets), and other functionality.
                  </li>
                  <li>
                    <span className="font-medium">Firebase:</span> For real-time database functionality, authentication, and hosting.
                  </li>
                  <li>
                    <span className="font-medium">Netlify:</span> For application hosting and serverless functions.
                  </li>
                </ul>
                <p>
                  Your use of such third-party services may be subject to additional terms and conditions provided by the respective third parties. 
                  It is your responsibility to review and comply with such terms.
                </p>
                <p>
                  If you access third-party services through our services, you do so at your own risk, and you understand that these Terms of Service 
                  and our Privacy Policy do not apply to your use of such sites. You expressly relieve JLS Trading Co from any and all liability arising 
                  from your use of any third-party website, service, or content.
                </p>
              </div>
            </section>

            {/* Limitation of Liability Section */}
            <section id="liability">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Limitation of Liability</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  In no event shall JLS Trading Co, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, 
                  incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other 
                  intangible losses, resulting from:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Your access to or use of or inability to access or use the services;
                  </li>
                  <li>
                    Any conduct or content of any third party on the services;
                  </li>
                  <li>
                    Any content obtained from the services; and
                  </li>
                  <li>
                    Unauthorized access, use or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence) 
                    or any other legal theory, whether or not we have been informed of the possibility of such damage.
                  </li>
                </ul>
                <p>
                  In jurisdictions where the exclusion or limitation of liability for consequential or incidental damages is not allowed, our liability 
                  shall be limited to the maximum extent permitted by law.
                </p>
              </div>
            </section>

            {/* Termination Section */}
            <section id="termination">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Termination</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  We may terminate or suspend your account and bar access to the services immediately, without prior notice or liability, under our sole 
                  discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms of Service.
                </p>
                <p>
                  If you wish to terminate your account, you may simply discontinue using the services, or contact us to request account deletion.
                </p>
                <p>
                  All provisions of the Terms of Service which by their nature should survive termination shall survive termination, including, without 
                  limitation, ownership provisions, warranty disclaimers, indemnity and limitations of liability.
                </p>
              </div>
            </section>

            {/* Changes to Terms Section */}
            <section id="changes">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Changes to Terms</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  We reserve the right, at our sole discretion, to modify or replace these Terms of Service at any time. If a revision is material we will 
                  provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole 
                  discretion.
                </p>
                <p>
                  By continuing to access or use our services after any revisions become effective, you agree to be bound by the revised terms. If you do 
                  not agree to the new terms, you are no longer authorized to use the services.
                </p>
              </div>
            </section>

            {/* Governing Law Section */}
            <section id="governing-law">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Governing Law</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  These Terms shall be governed and construed in accordance with the laws of North Carolina, United States, without regard to its conflict 
                  of law provisions.
                </p>
                <p>
                  Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these 
                  Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.
                </p>
                <p>
                  These Terms of Service constitute the entire agreement between us regarding our services and supersede and replace any prior agreements 
                  we might have had between us regarding the services.
                </p>
              </div>
            </section>

            {/* Contact Information Section */}
            <section id="contact">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Contact Information</h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  If you have any questions about these Terms of Service, please contact us at:
                </p>
                <div>
                  <p className="font-medium">JLS Trading Co</p>
                  <p>5301 Terminal Street</p>
                  <p>Charlotte NC 28208</p>
                  <p className="mt-2">
                    <span className="font-medium">Email:</span>{' '}
                    <a 
                      href="mailto:rdhpndh@gmail.com" 
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      rdhpndh@gmail.com
                    </a>
                  </p>
                  <p className="mt-2">
                    <span className="font-medium">Phone:</span>{' '}
                    <a 
                      href="tel:8002091869" 
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      800-209-1869
                    </a>
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              © {new Date().getFullYear()} JLS Trading Co. All rights reserved.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
